import { useState } from 'react';

import { useAuthInfo } from '@propelauth/react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { useCreateChronology } from '@/api/mutations/useCreateChronology';
import { useGetChronologiesForSubset } from '@/api/queries/useGetChronologiesForSubset';
import { Icon } from '@/new/components/icon';
import { Button, type ButtonProps } from '@/new/components/shadcn/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/new/components/shadcn/dropdown-menu';
import { cn } from '@/new/lib/utils';
import { addFactsBatchToVisualisation } from '@/new/pages/matters/_matterId/subsets/_subsetId/chronologies/components/visual-timeline/syncing-helpers/syncVisualisationWithTimelineChanges';
import { AddToChronologyDialog } from '@/new/pages/matters/_matterId/subsets/_subsetId/facts/components/dialogs/add-to-chronology-dialog';

interface FactChronologyDropdownProps {
  matterId: string;
  subsetId: string;
  selectedFactIds: Set<string>;
  disabled?: boolean;
  createTitlePrefix?: string;
  size?: 'sm' | 'default' | 'lg';
  triggerText?: string;
  variant?: ButtonProps['variant'];
}

export function FactChronologyDropdown({
  matterId,
  subsetId,
  selectedFactIds,
  disabled,
  createTitlePrefix,
  size,
  triggerText = 'Chronology',
  variant = 'default',
}: FactChronologyDropdownProps) {
  const navigate = useNavigate();
  const authInfo = useAuthInfo();

  const [isAddToChronologyDialogOpen, setIsAddToChronologyDialogOpen] = useState(false);

  const { data: chronologies, isLoading: isLoadingChronologies } = useGetChronologiesForSubset({
    matterId,
    subsetId,
  });

  const { createChronology } = useCreateChronology({
    matterId,
    subsetId,
    onSuccess: (data) => {
      navigate(`/matters/${matterId}/subsets/${subsetId}/chronologies/${data.chronologyId}`, { replace: true });
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  const handleCreateChronology = async () => {
    await createChronology.mutateAsync({
      chronologyId: null,
      factIds: Array.from(selectedFactIds),
      titlePrefix: createTitlePrefix,
    });
  };

  const handleSelectChronology = async (chronologyId: string) => {
    setIsAddToChronologyDialogOpen(false);
    try {
      const result = await createChronology.mutateAsync({
        chronologyId,
        factIds: Array.from(selectedFactIds),
      });

      if (result.addedFacts && result.addedFacts.length > 0) {
        await addFactsBatchToVisualisation({
          matterId,
          subsetId,
          chronologyId,
          newFacts: result.addedFacts,
          getAccessToken: authInfo.tokens.getAccessToken,
        });
      }
    } catch (error) {
      console.error('Error adding facts to chronology:', error);
      toast.error('Failed to add facts to chronology');
    }
  };

  const isDisabled = disabled || selectedFactIds.size === 0 || createChronology.isLoading;

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button size={size} disabled={isDisabled} variant={variant}>
            {createChronology.isLoading ? (
              <Icon className="lucide--loader animate-spin ease-in-out" />
            ) : (
              <Icon className="lucide--git-fork" />
            )}
            {triggerText}
            <Icon className="lucide--chevron-down" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent side="bottom" className={cn(size === 'sm' && 'text-xs')}>
          <DropdownMenuItem className={cn(size === 'sm' && 'text-xs')} onClick={handleCreateChronology}>
            <Icon className="lucide--plus" /> Create New
          </DropdownMenuItem>
          <DropdownMenuItem
            className={cn(size === 'sm' && 'text-xs')}
            onClick={() => setIsAddToChronologyDialogOpen(true)}
          >
            <Icon className="lucide--git-branch-plus" /> Add to Existing
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <AddToChronologyDialog
        isOpen={isAddToChronologyDialogOpen}
        onOpenChange={setIsAddToChronologyDialogOpen}
        isLoadingChronologies={isLoadingChronologies}
        chronologies={chronologies ?? []}
        handleSelectChronology={handleSelectChronology}
        isAddingFacts={createChronology.isLoading}
      />
    </>
  );
}
